import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, Play, Award, CheckCircle } from 'lucide-react';

export default function CustomerCourses() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/customer/courses');
        setEnrollments(res.data.courses || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" /></div>;

  return (
    <div data-testid="customer-courses-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Kursus Saya</h1>
        <span className="text-sm text-slate-500">{enrollments.length} kursus</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {enrollments.map(e => {
          const isCompleted = e.progress >= 100;
          return (
            <Card key={e.id} className="border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="h-32 bg-gradient-to-br from-[var(--store-primary, #2D6CDF)]/10 to-[#10B981]/10 flex items-center justify-center relative">
                {e.thumbnail ? (
                  <img src={e.thumbnail} alt={e.course_title} className="w-full h-full object-cover" />
                ) : (
                  <GraduationCap size={40} className="text-[var(--store-primary, #2D6CDF)]/30" />
                )}
                {isCompleted && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-emerald-500"><CheckCircle size={12} className="mr-1" /> Selesai</Badge>
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 line-clamp-2">{e.course_title}</h3>
                <p className="text-sm text-slate-500 mt-1">{e.instructor || 'No instructor'}</p>
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Progress</span>
                    <span className={isCompleted ? 'text-emerald-600 font-medium' : ''}>{e.progress || 0}%</span>
                  </div>
                  <Progress value={e.progress || 0} className="h-1.5" indicatorClassName={isCompleted ? 'bg-emerald-500' : ''} />
                </div>
                <div className="flex gap-2 mt-4">
                  <Link to={`/my/courses/${e.course_id}`} className="flex-1">
                    <Button className="w-full bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]" size="sm" data-testid={`continue-course-${e.id}`}>
                      <Play size={14} className="mr-1" /> {isCompleted ? 'Review' : 'Lanjutkan'}
                    </Button>
                  </Link>
                  {isCompleted && e.certificate_id && (
                    <Link to={`/my/certificates/${e.certificate_id}`}>
                      <Button variant="outline" size="sm" className="text-amber-600 border-amber-200">
                        <Award size={14} />
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!enrollments.length && (
          <div className="col-span-full py-12 text-center">
            <GraduationCap size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-400">Belum ada kursus</p>
            <p className="text-sm text-slate-400 mt-1">Mulai belajar dengan mendaftar kursus baru</p>
          </div>
        )}
      </div>
    </div>
  );
}
